import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types';
import Page from "components/Page";
import useSearch from "hooks/useSearch";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import PurchasingGroupStage1Service from "services/PurchasingGroupStage1Service";
import Pagination from "components/Pagination";
import GridBlock from "components/GridBlock";
import Tr from "translations/Translations";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import WorkableIconPreviewButton from "components/button/WorkableIconPreviewButton";
import BoldTypography from "components/BoldTypography";
import {makeStyles} from "@material-ui/core";
import moment from "moment";
import DateFormats from "constants/DateFormats";
import Loading from "components/Loading";
import Workspace from "components/Workspace";
import RouterPath from "libs/RouterPath";
import {PurchasingGroupStatus, PurchasingGroupStatusLabel} from "../constants/PurchasingGroupStatus";
import PurchasingGroupSearch from "./PurchasingGroupSearch";
import Button from "components/Button";

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2)
    },
    numberColumn: {
        width: 120,
        wordBreak: 'break-all'
    },
    typeColumn: {
        width: 170
    },
    dateTimeColumn: theme.components.table.dateTimeColumn,
    buttonColumn: theme.components.table.buttonColumn,
    nameColumn: {
        wordBreak: "break-all",
        paddingRight: theme.spacing(2),
    },
}));

function PurchasingGroupList(
    {
        match,
        member
    }
) {
    const classes = useStyles();
    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig, setSortConfig} = useSearch({
        sort: {
            column: 'dateInserted',
            direction: 'desc',
        }
    });
    const [purchasingGroupList, setPurchasingGroupList] = useState(false);
    const [actions, setActions] = useState({});
    const [working, setWorking] = useState();

    useEffect(() => {
        if (!member)
            PurchasingGroupStage1Service.getPurchasingGroupListActions().then((resp) => setActions(resp || {}))
    }, [member])

    useEffect(() => {
        setWorking(true);
        const promise = member ? PurchasingGroupStage1Service.getPurchasingGroupMemberList(searchData) : PurchasingGroupStage1Service.getPurchasingGroupList(searchData);
        promise.then((resp) => {
            setTotalCount(resp.totalCount)
            setPurchasingGroupList(resp);
        }).finally(() => setWorking(false));
        // eslint-disable-next-line
    }, [searchData]);

    useDidUpdateEffect(() => {
        if (purchasingGroupList?.totalCount !== undefined) {
            setTotalCount(purchasingGroupList.totalCount)
        }
    }, [purchasingGroupList]);

    const goToGroup = (x, section) => {
        let stage = 2;
        if (PurchasingGroupStatus.PREPARING === x.status || (member && x.lastStageStarted < 5)) {
            stage = 1;
        }

        const path = section ? RouterPath.createPath(RouterPath.purchasingGroup.MP_PURCHASING_GROUP_GROUP_ID_STAGE_NR_SECTION, {
                id: x.identity,
                stage: stage,
                section: section
            })
            :
            RouterPath.createPath(RouterPath.purchasingGroup.MP_PURCHASING_GROUP_GROUP_ID_STAGE_NR, {id: x.identity, stage: stage});
        RouterPath.redirect(path)
    }

    const handleSortChange = (id) => {
        let direction = 'desc';
        const active = searchData.sort.column === id;
        if (active) {
            direction = searchData.sort.direction === 'asc' ? 'desc' : 'asc';
        }

        setSortConfig({
            column: id,
            direction
        })
    }

    const getSortProps = (id) => {
        return {
            active: searchData.sort.column === id,
            direction: searchData.sort.direction,
            onClick: () => {
                handleSortChange(id)
            }
        }
    }

    const getHeaders = () => {
        const headers = [];
        headers.push({
            value: Tr('demand_number'),
            sort: getSortProps('number')
        });
        headers.push({
            value: Tr('name'),
            sort: getSortProps('name')
        });
        headers.push({
            value: Tr('add_data'),
            sort: getSortProps('dateInserted')
        });
        headers.push({
            value: Tr('status'),
            sort: getSortProps('state')
        });
        headers.push('');
        return headers;
    };

    return (<Page className={classes.root}>
        <Workspace
            title={Tr('purchase_groups')}
        >
            {
                !purchasingGroupList ? <Loading/> :
                    <>
                        <GridBlock justify='space-between'>
                            <PurchasingGroupSearch doSearch={setSearchConfig} searchInProgress={working}/>
                            {actions.hasAdd === true && <Button
                                onClick={() => {
                                    goToGroup({identity: 0, status: PurchasingGroupStatus.PREPARING}, 'basic-data')
                                }}
                            >
                                {Tr("purchase_group_add")}
                            </Button>}
                        </GridBlock>

                        <SimpleTable
                            headers={
                                getHeaders()
                            }
                            items={
                                purchasingGroupList.items.map((x, i) => {
                                    return [
                                        x.number,
                                        x.name,
                                        moment(x.dateModified).format(DateFormats.DATETIME_FORMAT),
                                        x.status ? PurchasingGroupStatusLabel(x.lastStageStarted < 5 ? PurchasingGroupStatus.PREPARING : x.status) : null,
                                        <WorkableIconPreviewButton tooltip={Tr('details')} onClick={() => goToGroup(x)}/>
                                    ]
                                })
                            }
                            columnClasses={
                                [
                                    classes.numberColumn,
                                    classes.nameColumn,
                                    classes.typeColumn,
                                    classes.typeColumn,
                                    classes.buttonColumn,
                                ]
                            }
                        />
                        {
                            !purchasingGroupList.items.length && <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
                        }
                        <Pagination config={searchData.pagination} setConfig={setPaginationConfig} totalCount={totalCount} disabled={working}/>
                    </>
            }
        </Workspace>
    </Page>)
}

PurchasingGroupList.propTypes = {
    classes: PropTypes.any,
}

export default PurchasingGroupList;